import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/ui/button';
import DropdownMenu from '../components/ui/dropdown-menu-new';
import { CheckCircle, Check, ArrowRight, ChevronDown, User, Lock, Settings, LogOut } from 'lucide-react';

const Header = ({ variant = 'default' }) => {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/home');
  };

  // Removed unused isActive function

  if (loading) {
    return null; // Don't render header while loading
  }

  return (
    <nav className="border-b bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-2">
            <Link to="/home" className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center relative shadow-sm">
                <CheckCircle className="w-5 h-5 text-primary-foreground" />
                <div className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-green-500 rounded-full flex items-center justify-center border-2 border-background">
                  <Check className="w-2 h-2 text-white" />
                </div>
              </div>
              <span className="text-xl font-bold text-foreground tracking-tight">LLMChecks</span>
            </Link>
          </div>

          {user ? (
            // Authenticated navigation
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/blog"
                className={`text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 ${location.pathname.startsWith('/blog') ? 'text-foreground font-medium' : ''
                  }`}
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Blog
              </Link>
              <Link
                to="/dashboard"
                className={`text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 ${location.pathname.startsWith('/dashboard') ? 'text-foreground font-medium' : ''
                  }`}
              >
                <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                Dashboard
              </Link>
              <Link
                to="/website-checks"
                className={`text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 ${location.pathname.startsWith('/website-checks') ? 'text-foreground font-medium' : ''
                  }`}
              >
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                Website Checks
              </Link>
              <Link
                to="/evaluations"
                className={`text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 ${location.pathname.startsWith('/evaluations') ? 'text-foreground font-medium' : ''
                  }`}
              >
                <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                Evaluations
              </Link>
              <Link
                to="/team-invitations"
                className={`text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 ${location.pathname.startsWith('/team') ? 'text-foreground font-medium' : ''
                  }`}
              >
                <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                Team
              </Link>
              {user.role === 'super_admin' && (
                <Link
                  to="/admin"
                  className={`text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2 ${location.pathname.startsWith('/admin') ? 'text-foreground font-medium' : ''
                    }`}
                >
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  Admin
                </Link>
              )}
              <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                  <button className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1">
                    {user.name} <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="w-56">
                  <DropdownMenu.Label>My Account</DropdownMenu.Label>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item asChild>
                    <Link to="/profile" className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      Profile Settings
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item asChild>
                    <Link to="/change-password" className="flex items-center">
                      <Lock className="mr-2 h-4 w-4" />
                      Change Password
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item asChild>
                    <Link to="/organization-settings" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      Organization Settings
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator />
                  <DropdownMenu.Item
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu>
            </div>
          ) : (
            // Unauthenticated navigation
            <div className="hidden md:flex items-center space-x-8">
              <Link
                to="/blog"
                className="text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                Blog
              </Link>
              <Button variant="outline" size="sm" asChild>
                <Link to="/login">Sign In</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to="/register" className="flex items-center">
                  Try Free <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
          )}

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button variant="ghost" size="sm">
              Menu
            </Button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Header;
